import React, { useState } from "react";
import { StaffDialogProps, StaffFormData } from "../types/staff";
import GenericForm, { FormFieldConfig } from "../../../../components/genericform/GenericForm";
import ImageUpload from "../../products/components/ImageUpload";
import { notifySuccess } from "src/assets/toastService";

interface AddStaffDialogProps extends StaffDialogProps {
  onAddStaff: (formData: StaffFormData) => Promise<boolean>;
}

const AddStaffDialog: React.FC<AddStaffDialogProps> = ({ open, onClose, onAddStaff }) => {
  const initialState: StaffFormData = {
    staff_name: "",
    phone_number: "",
    image: null,
  };

  const [formData, setFormData] = useState<StaffFormData>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageChange = (file: File | null) => {
    setFormData((prev) => ({ ...prev, image: file }));
  };

  const handlePhoneChange = (val: string) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 8);
    setFormData((prev) => ({ ...prev, phone_number: cleaned }));
  };

  const fields: FormFieldConfig[] = [
    {
      id: "staff_name",
      label: "Name",
      required: true,
      value: formData.staff_name,
      onChange: (val: string) =>
        setFormData((prev) => ({
          ...prev,
          staff_name: val.slice(0, 25),
        })),
    },
    {
      id: "phone_number",
      label: "Phone Number",
      type: "text",
      required: true,
      value: formData.phone_number,
      onChange: handlePhoneChange,
    },
  ];

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const success = await onAddStaff(formData);

      if (success) {
        // Small delay to ensure toast is shown before closing
        setTimeout(() => {
          notifySuccess(`Staff member "${formData.staff_name}" added successfully.`);
          setFormData(initialState);
          onClose();
          setIsSubmitting(false);
        }, 500);
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData(initialState);
      onClose();
    }
  };

  const isFormValid = () => {
    return formData.staff_name.trim() !== "" && formData.phone_number.trim().length === 8;
  };

  return (
    <GenericForm open={open} title="Enter Staff Details" fields={fields} onClose={handleClose} onSubmit={handleSubmit} isFormValid={isFormValid} confirmLabel="Confirm" cancelLabel="Cancel">
      <div className="staff-image-input mt-3">
        <ImageUpload value={formData.image} onChange={handleImageChange} />
      </div>
    </GenericForm>
  );
};

export default AddStaffDialog;
